'use client';

import React from 'react';
import Link from 'next/link';
import { ArrowRight, Sparkles, Zap } from 'lucide-react';

interface BiharHeaderProps {
  title: string;
  subtitle?: string;
  description?: string;
  hindiTitle?: string;
  hindiSubtitle?: string;
  hindiDescription?: string;
  badgeText?: string;
  badgeIcon?: React.ReactNode;
  hideBadge?: boolean;
  showBackButton?: boolean;
  backUrl?: string;
  backText?: string;
  ctaButton?: {
    text: string;
    hindiText?: string;
    href: string;
    icon?: React.ReactNode;
  };
}

export default function BiharHeader({
  title,
  subtitle,
  description,
  hindiTitle,
  hindiSubtitle,
  hindiDescription,
  badgeText = "Teachers Service Portal",
  badgeIcon = <Sparkles className="h-3 w-3 sm:h-4 sm:w-4 text-zinc-600 mr-2 animate-spin" />,
  hideBadge = false,
  showBackButton = false,
  backUrl = "/bihar",
  backText = "Back to Home",
  ctaButton
}: BiharHeaderProps) {
  return (
    <div className="relative overflow-hidden">
      {/* Soft colored backdrop */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-emerald-50"></div>
      <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%239C92AC%22%20fill-opacity%3D%220.04%22%3E%3Ccircle%20cx%3D%2230%22%20cy%3D%2230%22%20r%3D%222%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-40"></div>
      {/* Soft accent glows */}
      <div className="pointer-events-none absolute -top-24 -left-16 w-72 h-72 bg-blue-300/25 rounded-full blur-3xl"></div>
      <div className="pointer-events-none absolute -bottom-24 -right-10 w-80 h-80 bg-emerald-300/25 rounded-full blur-3xl"></div>
      <div className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[40rem] h-40 bg-purple-200/15 rounded-full blur-3xl"></div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-2 sm:py-3 lg:py-4 relative">
        {/* Back Button */}
        {showBackButton && (
          <div className="mb-2 sm:mb-3">
            <Link 
              href={backUrl} 
              className="inline-flex items-center text-zinc-700 hover:text-zinc-900 transition-colors duration-300 font-medium"
            >
              <ArrowRight className="h-4 w-4 mr-2 rotate-180" />
              {backText}
            </Link>
          </div>
        )}

        <div className="text-center mb-2 sm:mb-3 lg:mb-4">
          {/* Enhanced Animated Badge */}
          {!hideBadge && (
            <div className="inline-flex items-center px-3 py-1.5 sm:px-4 sm:py-2 bg-white/70 backdrop-blur-sm rounded-full border border-zinc-200 mb-3 sm:mb-4 hover:scale-105 transition-transform duration-300 cursor-default shadow-sm">
              {badgeIcon}
              <span className="text-xs sm:text-sm font-medium text-zinc-900">{badgeText}</span>
              <Zap className="h-3 w-3 sm:h-4 sm:w-4 text-zinc-600 ml-2 animate-pulse" />
            </div>
          )}
          
          {/* Enhanced Title with Mobile Optimization */}
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-extrabold bg-gradient-to-r from-zinc-700 via-zinc-800 to-zinc-900 bg-clip-text text-transparent mb-3 sm:mb-4 leading-tight animate-fade-in px-2">
            {title}
            {hindiTitle && (
              <span className="block text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl bg-gradient-to-r from-zinc-500 to-zinc-600 bg-clip-text text-transparent mt-1 font-medium">
                {hindiTitle}
              </span>
            )}
            {subtitle && (
              <span className="block text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl bg-gradient-to-r from-zinc-600 to-zinc-700 bg-clip-text text-transparent mt-1 sm:mt-2 animate-slide-up">
                {subtitle}
              </span>
            )}
            {hindiSubtitle && (
              <span className="block text-lg sm:text-xl md:text-2xl lg:text-3xl xl:text-4xl bg-gradient-to-r from-zinc-500 to-zinc-600 bg-clip-text text-transparent mt-1 font-medium">
                {hindiSubtitle}
              </span>
            )}
          </h1>
          
          {/* Enhanced Description */}
          {description && (
            <p className="text-base sm:text-lg lg:text-xl xl:text-2xl text-zinc-800 max-w-4xl mx-auto leading-relaxed mb-4 sm:mb-6 px-4 animate-fade-in-delay">
              {description}
            </p>
          )}
          {hindiDescription && (
            <p className="text-sm sm:text-base lg:text-lg xl:text-xl text-zinc-700 max-w-4xl mx-auto leading-relaxed mb-4 sm:mb-6 px-4 animate-fade-in-delay font-medium">
              {hindiDescription}
            </p>
          )}
          
          {/* CTA Button */}
          {ctaButton && (
            <div className="flex justify-center mt-6 sm:mt-8">
              <Link 
                href={ctaButton.href} 
                className="group inline-flex flex-col items-center px-8 py-4 sm:px-10 sm:py-5 bg-gradient-to-r from-zinc-700 to-zinc-800 text-white font-bold text-lg sm:text-xl lg:text-2xl rounded-xl hover:from-zinc-800 hover:to-zinc-900 transition-all duration-300 hover:scale-105 hover:shadow-xl animate-pulse"
              >
                <div className="flex items-center">
                  {ctaButton.icon}
                  {ctaButton.text}
                  <ArrowRight className="h-5 w-5 sm:h-6 sm:w-6 ml-3 group-hover:translate-x-1 transition-transform duration-300" />
                </div>
                {ctaButton.hindiText && (
                  <span className="text-sm sm:text-base font-medium mt-2 opacity-90">{ctaButton.hindiText}</span>
                )}
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
} 